import {
  Box,
  ImportCard,
  Inventory,
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import { AxiosRequestConfig } from 'axios';
import { getGraphQLQueryClient } from './query-client';
import { gql } from 'graphql-request';
import axiosClient from './request-client';

export const BOX_TYPES = [
  { id: 1, label: 'Carpeta', key: 'FOLDER', icon: 'folder' },
  { id: 2, label: 'Caja', key: 'BOX', icon: 'archive' },
  { id: 3, label: 'Deckbox', key: 'DECKBOX', icon: 'inbox' },
  { id: 4, label: 'Lata', key: 'TIN', icon: 'cilinder' },
  { id: 5, label: 'Otro', key: 'OTHER', icon: 'box' },
];

export const listBoxes = (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Box>>(
    gql`
      query warehouses($filters: WarehouseFilters) {
        data: warehouses(filters: $filters) {
          data {
            id
            name
            description
            type
            customType
            capacity
            isDefault

            inventory {
              id
              quantity
            }

            createdAt
            updatedAt
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    variables
  );

export const getBox = (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Box>>(
    gql`
      query ($id: Int!) {
        data: warehouse(id: $id) {
          id
          name
          description
          type
          customType
          capacity

          inventory {
            id
            konamiId
            quantity
            language
            rarity
            state
            salable

            cardSet {
              id
              code
              rarity
              image

              lastCardMarket
              lastCardTrader
              lastTcgPlayer

              card {
                name
              }

              set {
                konamiName
              }
            }
          }

          createdAt
          updatedAt
        }
      }
    `,
    variables
  );

export const createBox = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($input: CreateWarehouseInput!) {
        data: createWarehouse(input: $input) {
          id
          name
          description
        }
      }
    `,
    variables
  );

export const updateBox = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($id: Int!, $input: UpdateWarehouseInput!) {
        data: updateWarehouse(id: $id, input: $input) {
          id
          name
          description
        }
      }
    `,
    variables
  );

export const deleteBox = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($id: Int!) {
        data: deleteWarehouse(id: $id) {
          id
        }
      }
    `,
    variables
  );

export const clearBox = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($id: Int!) {
        data: clearWarehouse(id: $id) {
          id
        }
      }
    `,
    variables
  );

export const addInventory = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($warehouseId: Int!, $input: AddInventoryInput!) {
        data: addInventory(warehouseId: $warehouseId, input: $input) {
          id
        }
      }
    `,
    variables
  );

export const addInventoryBulk = (
  user: Nullable<UserWithToken>,
  variables: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($items: [AddInventoryBulkItem!]!) {
        data: addInventoryBulk(items: $items) {
          id
        }
      }
    `,
    variables
  );

export const editInventoryItem = (
  user: Nullable<UserWithToken>,
  { itemId, input }: { itemId: number; input: Record<string, unknown> }
) =>
  getGraphQLQueryClient(user).request<Box>(
    gql`
      mutation ($itemId: Int!, $input: EditInventoryItemInput!) {
        data: editInventoryItem(itemId: $itemId, input: $input) {
          id
        }
      }
    `,
    {
      itemId,
      input,
    }
  );

export const importList = (
  body: FormData,
  config?: AxiosRequestConfig
): Promise<{ createdCards: Inventory[]; nonCreatedCards: ImportCard[] }> =>
  axiosClient.post(`/imports/inventory-cards`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  });

export const syncList = (
  body: FormData,
  config?: AxiosRequestConfig
): Promise<{
  createdCards: Inventory[];
  nonCreatedCards: ImportCard[];
  updatedCards: Inventory[];
}> =>
  axiosClient.post(`/imports/sync-inventory-cards`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  });
